import { Injectable } from '@angular/core';
import { CalendarService } from '../../../../../../../services/calendar.service';
import { TimeBlockCrudService } from '../../../../../crud/time-block-crud.service';
import { ITimeBlockComponentItem } from '../../../../../types/time-block-component-item-types';
import { DateTimeHelper } from '../../../../../../../util/date-time-helper';
import { DragAndDropHandlerService } from './drag-and-drop-handler.service';
import { TimeBlockElementSelectorService } from '../../../../../rendering/time-block-element-selector.service';
import {
  TimeBlockDayOrWeekFixIntervalType,
  TimeBlockDayOrWeekVariableIntervalType,
} from '../../../../../../../../../core/models/timeblock/time-block-view-type.model';
import { TimeBlockDragResizeControllerService } from '../../time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { ShiveRenderService } from '../../../../../../../../../core/services/rendering/shive-render.service';

@Injectable()
export class DragDropPositioningService {
  constructor(
    private readonly shiveRenderService: ShiveRenderService,
    private readonly calendarService: CalendarService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly dragAndDropHandlerService: DragAndDropHandlerService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
  ) {}

  /**
   * For day or week variable interval time blocks
   */
  public deployDayOrWeekVariableIntervalTimeBlocks(
    event: Interact.DragEvent,
    draggedTimeBlocks: ITimeBlockComponentItem[],
  ): void | never {
    const sourceContainerId = this.dragAndDropHandlerService.sourceContainerId;
    const targetContainerId = this.dragAndDropHandlerService.targetContainerId;

    const sourceContainer = this.calendarService.model.timeBlockHtmlContainers[sourceContainerId];
    const targetContainer = this.calendarService.model.timeBlockHtmlContainers[targetContainerId];

    // If user drops the time block with right mouse button, target container is undefined. Therefore, do nothing.
    if (typeof sourceContainer === 'undefined' || typeof targetContainer === 'undefined') {
      throw new Error('Container is undefined.');
    }

    draggedTimeBlocks.forEach((timeBlockPart) => {
      // / If block is dropped between drop zones, move it to the corresponding drop zone.
      if (event && !event.modifiers[0].inRange) {
        const originLeft = sourceContainer.nativeElement.getBoundingClientRect().left;
        const targetLeft = targetContainer.nativeElement.getBoundingClientRect().left;

        const translateX = targetLeft - originLeft;
        const timeBlockHTMLWrapper =
          TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlockPart);
        this.shiveRenderService.moveElement(timeBlockHTMLWrapper, translateX, 0);
      }
    });

    const draggedTimeBlock = this.timeBlockDragResizeControllerService.TransformationTimeBlock;
    const daysMoved = targetContainerId - sourceContainerId;
    const newStartDate = DateTimeHelper.addDays(draggedTimeBlock.timeBlockModel.start, daysMoved);
    const newEndDate = DateTimeHelper.addDays(draggedTimeBlock.timeBlockModel.end, daysMoved);

    const timeBlockViewType = draggedTimeBlock.timeBlockModel
      .timeBlockViewType as TimeBlockDayOrWeekVariableIntervalType;
    timeBlockViewType.laneIndex = targetContainerId;

    // Reset the geometry data
    timeBlockViewType.geometryData.width = 100;
    timeBlockViewType.geometryData.containerLeftEdgeToTimeBlockLeftEdge = 0;

    draggedTimeBlock.timeBlockModel.start = newStartDate;
    draggedTimeBlock.timeBlockModel.end = newEndDate;
    this.timeBlockCrudService.insertTimeBlock(draggedTimeBlock);
  }

  /**
   * For day or week full day time blocks
   */
  public deployFixIntervalTimeBlock(draggedTimeBlock: ITimeBlockComponentItem): void {
    const sourceContainerId = this.dragAndDropHandlerService.sourceContainerId;
    const targetContainerId = this.dragAndDropHandlerService.targetContainerId;

    if (sourceContainerId === null || targetContainerId === null) {
      throw new Error('Container id is not valid.');
    }

    const isDayOrWeekFixIntervalType =
      draggedTimeBlock.timeBlockModel.timeBlockViewType instanceof
      TimeBlockDayOrWeekFixIntervalType;

    const timeBlockModel = draggedTimeBlock.timeBlockModel;
    // Don't update start- and end date for full day month view time blocks since they are already up do date.
    const daysMoved = isDayOrWeekFixIntervalType ? targetContainerId - sourceContainerId : 0;
    const newStartDate = DateTimeHelper.addDays(timeBlockModel.start, daysMoved);
    const newEndDate = DateTimeHelper.addDays(timeBlockModel.end, daysMoved);

    this.insertDayOrWeekHorizontalTimeBlock(
      draggedTimeBlock,
      newStartDate,
      newEndDate,
      targetContainerId,
    );
  }

  private insertDayOrWeekHorizontalTimeBlock(
    draggedTimeBlock: ITimeBlockComponentItem,
    newStartDate: Date,
    newEndDate: Date,
    targetContainerId: number,
  ): void {
    const timeBlockModel = draggedTimeBlock.timeBlockModel;
    const timeBlockViewType = timeBlockModel.timeBlockViewType as TimeBlockDayOrWeekFixIntervalType;
    timeBlockModel.start = newStartDate;
    timeBlockModel.end = newEndDate;
    timeBlockViewType.laneIndexStart = targetContainerId;
    timeBlockViewType.laneIndexEnd =
      targetContainerId + DateTimeHelper.differenceInDays(timeBlockModel.end, timeBlockModel.start);
    this.timeBlockCrudService.insertTimeBlock(draggedTimeBlock);
  }
}
